import ApiModel                from '@widesk-core/models/ApiModel';
import cacheSystem             from '@widesk-core/models/apiCacheSystem';
import _get                    from 'lodash/get';
import { batchIdPlaceholder }  from '@widesk-core/models/batch/types';
import { BatchMethod }         from '@widesk-core/models/batch/types';
import { BatchRequestItem }    from '@widesk-core/models/batch/types';
import { BatchResponseItem }   from '@widesk-core/models/batch/types';

/**
 * Même substitution que le `Utils.format` de modelx (que le paquet n'exporte pas) : `{attr}` est
 * obligatoire, `{?attr}` vaut la chaîne vide quand l'attribut manque. D'où le `/` final à retirer
 * pour une création, où `/places/{?id}` donne `/places/`.
 */
const formatPath = (template: string, attributes: Record<string, unknown>) => template
	.replace(/\{([?]?)([^}]+)\}/g, (match, optional, name) => {
		return String(_get(attributes, name, optional === '?' ? '' : match));
	})
	.replace(/\/$/, '');

/** Les filtres d'une route, sérialisés comme axios le fait pour un `params` : un tableau en `nom[]`. */
const formatQuery = (params: Record<string, unknown>) => {
	const search = new URLSearchParams();

	Object.entries(params).forEach(([name, value]) => {
		if (value === undefined || value === null) return;

		if (Array.isArray(value)) value.forEach(item => search.append(`${name}[]`, String(item)));
		else search.append(name, String(value));
	});

	return search.toString();
};

/** Les headers du model (locale, fichiers...) tels que le protocole batch les accepte : des chaînes. */
const stringifyHeaders = (headers: RequestCustomHeaders): Record<string, string> => {
	const entries = Object.entries(headers)
		.filter(([, value]) => typeof value !== 'undefined')
		.map(([name, value]) => [name, Array.isArray(value) ? value.join(',') : String(value)]);

	return Object.fromEntries(entries);
};

export type BatchOperationOptions<M extends ApiModel> = {
	index: number;
	model: M;
	method: BatchMethod;

	/**
	 * L'id visé par la route. Absent pour une création ; c'est un jeton `[:$N.id]` quand l'opération
	 * porte sur un model créé plus tôt DANS LE MÊME LOT, dont l'id n'existera qu'à l'exécution.
	 */
	targetId?: id;

	/** Les attributs envoyés. `undefined` pour un DELETE, une liste pour une écriture de collection. */
	attributes?: Record<string, unknown> | unknown[];

	/** Les paramètres portés par la route. Un objet précis, lui, est désigné par `targetId`. */
	params?: Record<string, unknown>;

	/** Suffixe ajouté au path de la ressource, pour `POST /features/batch`. */
	pathSuffix?: string;

	/** `false` pour une écriture de collection : la réponse ne porte pas UN objet à reporter. */
	hydrate?: boolean;
};

/**
 * Une opération du lot, et la poignée que `BatchManager` rend pour la référencer.
 *
 * Elle sert deux temps : AVANT l'exécution, `id` et `urn` rendent le jeton de substitution, ce qui
 * permet de rattacher une requête à un objet que le lot n'a pas encore créé ; APRÈS, elles rendent
 * les valeurs réelles, et `model` est hydraté — c'est le même model que l'appelant a passé, donc la
 * suite du code continue de travailler avec lui comme après un `patch()` classique.
 */
export default class BatchOperation<M extends ApiModel = ApiModel> {
	public readonly index: number;
	public readonly model: M;
	public readonly method: BatchMethod;
	public readonly attributes: Record<string, unknown> | unknown[] | undefined;

	private readonly _targetId: id | undefined;
	private readonly _params: Record<string, unknown> | undefined;
	private readonly _pathSuffix: string | undefined;
	private readonly _hydrate: boolean;
	private _response: BatchResponseItem | undefined;

	public constructor(options: BatchOperationOptions<M>) {
		this.index = options.index;
		this.model = options.model;
		this.method = options.method;
		this.attributes = options.attributes;
		this._targetId = options.targetId;
		this._params = options.params;
		this._pathSuffix = options.pathSuffix;
		this._hydrate = options.hydrate !== false;
	}

	/** L'`id` du model : le jeton de substitution tant que le lot n'a pas été joué, l'id réel ensuite. */
	public get id(): id {
		return this.model.id || batchIdPlaceholder(this.index);
	}

	/**
	 * L'urn du model, à passer en valeur de relation dans le corps d'une AUTRE requête du lot
	 * (`{ place: place.urn }`). L'API ne sait substituer que l'`id`, l'urn est donc reconstruite
	 * autour du jeton : `$:map:place:[:$0.id]`.
	 */
	public get urn(): Urn {
		if (this.model.urn) return this.model.urn;

		const { partition, service, resource } = this.model.urnData;

		return `${partition}:${service}:${resource}:${batchIdPlaceholder(this.index)}` as Urn;
	}

	/** La réponse de l'API pour cette opération, une fois le lot joué. */
	public get response() {
		return this._response;
	}

	public get status() {
		return this._response?.status;
	}

	public get isSuccess() {
		const status = this.status;

		return typeof status === 'number' && status >= 200 && status < 300;
	}

	/** Le message d'erreur renvoyé par l'API pour cette opération, quand elle a échoué. */
	public get errorMessage(): string | undefined {
		const body = this._response?.body;

		if (!body) return undefined;

		return body['description'] || body['hydra:description'] || body['detail'] || body['title'];
	}

	/**
	 * La requête envoyée à l'API. `servicePath` préfixe la route, que le batch attend absolue ;
	 * `clientHeaders` vient de `BatchConnector.subRequestHeaders`.
	 */
	public toRequestItem(servicePath: string, clientHeaders: Record<string, string> = {}): BatchRequestItem {
		const { path: template } = this.model.constructor as unknown as { path: string };
		const path = formatPath(template, { ...this.model.attributes, id: this._targetId })
			+ (this._pathSuffix ? `/${this._pathSuffix}` : '');
		const query = this._params ? formatQuery(this._params) : '';

		return {
			method: this.method,
			route: servicePath + path + (query ? `?${query}` : ''),
			body: this.attributes ? JSON.stringify(this.attributes) : null,
			headers: {
				...clientHeaders,
				...this._contentTypeHeader(),
				// Même règle que `ApiConnectorV4.save` : un corps qui porte des `translations` s'adresse à
				// toutes les langues. Les headers du client axios ne valent que pour l'enveloppe du lot,
				// chaque sous-requête porte les siens.
				...(!Array.isArray(this.attributes) && this.attributes?.['translations'] ? { 'X-LOCALE': '*' } : {}),
				...stringifyHeaders(this.model.customHeaders),
			},
		};
	}

	/** Purge du cache tout ce qui concerne la ressource du model, listes mémorisées comprises. */
	private _removeResourceFromCache() {
		const { partition, service, resource } = this.model.urnData;

		cacheSystem.removeUrnStartWith(`${partition}:${service}:${resource}:`);
	}

	/**
	 * Le `Content-Type` que le service attend selon la méthode, comme `ApiConnectorV4.save` le pose
	 * pour un appel unitaire : API Platform refuse un PATCH qui n'est pas en merge-patch.
	 */
	private _contentTypeHeader(): Record<string, string> {
		switch (this.method) {
			case 'PATCH':
				return { 'Content-Type': 'application/merge-patch+json' };
			case 'POST':
			case 'PUT':
				return { 'Content-Type': 'application/ld+json' };
			default:
				return {};
		}
	}

	/**
	 * Enregistre la réponse SANS toucher au model : décider si l'écriture a tenu regarde le lot
	 * entier, pas l'opération seule (voir `BatchManager.run`).
	 */
	public setResponse(response: BatchResponseItem | undefined) {
		this._response = response;
	}

	/**
	 * Reporte la réponse sur le model, exactement comme le connector le fait après un `save()` ou un
	 * `destroy()` unitaire : purge du cache puis réapplication des attributs renvoyés.
	 */
	public applyToModel() {
		if (!this.isSuccess) return;

		if (this.method === 'DELETE') {
			// Suppression par filtres : pas UN model à marquer détruit, mais des listes à oublier.
			if (this._params) {
				this._removeResourceFromCache();

				return;
			}

			cacheSystem.removeUrn(this.model.urn);

			this.model.isLoaded = false;
			this.model.isDestroyed = true;
			this.model.isDirty = false;
			this.model.collection?.remove(this.model);

			return;
		}

		// Un model modifié sort du cache ; un model créé n'y est pas encore, mais les LISTES de sa
		// ressource y sont et ne le contiennent pas — on purge donc toute la ressource.
		if (this.model.urn) cacheSystem.removeUrn(this.model.urn);
		else this._removeResourceFromCache();

		const body = this._response?.body;

		if (body && this._hydrate) {
			this.model.reset(body, { dirty: false });
			this.model.setIsLoaded(true);
		}
	}
}
